// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { useState, useEffect } from 'react'
import { ethers } from 'ethers'
import { Erc20Abi } from '@shared/Abis'

export interface UseTokenSymbolProps {
  tokenAddress: string
  provider?: ethers.Provider | null
  fallbackSymbol?: string
}

export function useTokenSymbol({ tokenAddress, provider, fallbackSymbol = 'TOKEN' }: UseTokenSymbolProps) {
  const [tokenSymbol, setTokenSymbol] = useState<string>(fallbackSymbol)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchTokenSymbol = async () => {
      if (!tokenAddress || !provider) {
        setTokenSymbol(fallbackSymbol)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const tokenContract = new ethers.Contract(tokenAddress, Erc20Abi, provider)
        const symbol = await tokenContract.symbol()
        setTokenSymbol(symbol)
      } catch (err) {
        setTokenSymbol(fallbackSymbol)
        setError(err instanceof Error ? err.message : 'Failed to fetch token symbol')
      } finally {
        setIsLoading(false)
      }
    }

    fetchTokenSymbol()
  }, [tokenAddress, provider, fallbackSymbol])

  return { tokenSymbol, isLoading, error }
}
